import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Icon from '@mui/material/Icon'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import { green, red } from '@mui/material/colors'

import { NumberInput, SelectField } from '../shared'
import {
    selectBattery,
    selectBatteryQty,
    selectRequiredInverters,
    selectTempSelectedInverter,
    selectIs3Phase,
    selectOffgrid,
    selectHybrid,
    selectStandardInverters,
    selectACController,
    setBattery,
    setBatteryQty,
    setRequiredInverters,
    addInverter,
    removeInverter,
    updateInverter,
    setTempSelectedInverter,
    setIs3Phase,
    setOffgrid,
    setHybrid,
    setStandardInverters,
    setACController
} from '../../store/slices/systemSlice'
import {
    selectBatteries,
    selectInverters,
    selectACControllers
} from '../../store/slices/catalogSlice'

// Styles
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2
    },
    sectionTitle: {
        fontSize: '13px',
        fontWeight: 'bold',
        borderBottom: '1px solid #ccc',
        pb: 0.5,
        mb: 1
    },
    row: {
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    checkboxRow: {
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap'
    },
    batterySelect: {
        minWidth: 280,
        fontSize: '12px'
    },
    inverterTable: {
        fontSize: '12px',
        '& th, & td': {
            padding: '4px 8px',
            fontSize: '12px'
        }
    },
    addRow: {
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        mb: 1
    }
}

// Group items by manufacturer
const groupByManufacturer = (items, idField = 'id') => {
    const grouped = {}
    items.forEach(item => {
        const manu = item.manufacturer || 'Other'
        if (!grouped[manu]) {
            grouped[manu] = []
        }
        grouped[manu].push(item)
    })
    return grouped
}

const BatteryInvertersTab = () => {
    const dispatch = useDispatch()

    // Selectors
    const battery = useSelector(selectBattery)
    const batteryQty = useSelector(selectBatteryQty)
    const requiredInverters = useSelector(selectRequiredInverters)
    const tempSelectedInverter = useSelector(selectTempSelectedInverter)
    const is3Phase = useSelector(selectIs3Phase)
    const offgrid = useSelector(selectOffgrid)
    const hybrid = useSelector(selectHybrid)
    const standardInverters = useSelector(selectStandardInverters)
    const acController = useSelector(selectACController)

    // Catalog data
    const batteries = useSelector(selectBatteries)
    const inverters = useSelector(selectInverters)
    const acControllers = useSelector(selectACControllers)

    // Handlers
    const handleBatteryChange = (event) => {
        const batteryId = event.target.value
        const selectedBattery = batteries.find(b => b.battery_id === batteryId)
        dispatch(setBattery(selectedBattery || null))
    }

    const handleBatteryQtyChange = (value) => {
        dispatch(setBatteryQty(value))
    }

    const handleInverterTypeChange = (type) => (event) => {
        const checked = event.target.checked
        switch (type) {
            case 'standardinverters':
                dispatch(setStandardInverters(checked))
                break
            case 'hybrid':
                dispatch(setHybrid(checked))
                break
            case 'offgrid':
                dispatch(setOffgrid(checked))
                break
            case 'is3phase':
                dispatch(setIs3Phase(checked))
                break
        }
    }

    const handleTempInverterChange = (event) => {
        dispatch(setTempSelectedInverter(event.target.value))
    }

    const handleAddInverter = () => {
        if (tempSelectedInverter && tempSelectedInverter > 0) {
            const inv = inverters.find(i => i.id === tempSelectedInverter)
            if (inv) {
                dispatch(addInverter({
                    inverter: inv,
                    is3phase: inv.is3phase,
                    solaredge: inv.manufacturer?.toLowerCase() === 'solaredge',
                    overwrite: true
                }))
                dispatch(setTempSelectedInverter(-1))
            }
        }
    }

    const handleRemoveInverter = (index) => {
        dispatch(removeInverter(index))
    }

    const handleOverwriteChange = (index) => {
        const inv = requiredInverters[index]
        dispatch(updateInverter({
            index,
            data: { overwrite: !inv.overwrite }
        }))
    }

    const handleACControllerChange = (event) => {
        const controllerId = event.target.value
        const selected = acControllers.find(c => c.item_id === controllerId)
        dispatch(setACController(selected || {}))
    }

    // Filter inverters based on selected types
    const filteredInverters = inverters.filter(inv => {
        if (is3Phase && !inv.is3phase) return false
        if (offgrid && !inv.offgrid) return false
        if (hybrid && !inv.hybrid) return false
        if (standardInverters && inv.hybrid) return false
        return true
    })

    // Group batteries and inverters by manufacturer
    const groupedBatteries = groupByManufacturer(batteries, 'battery_id')
    const groupedACControllers = groupByManufacturer(acControllers, 'item_id')

    // Calculate total system size
    const systemSize = requiredInverters.reduce((total, inv) => {
        return total + (inv.inverter?.watts || 0)
    }, 0)

    return (
        <Box sx={styles.container}>
            {/* Battery Section */}
            <Box>
                <Typography sx={styles.sectionTitle}>Battery Storage</Typography>
                <Box sx={styles.row}>
                    <Select
                        value={battery?.battery_id || 0}
                        onChange={handleBatteryChange}
                        size="small"
                        sx={styles.batterySelect}
                        displayEmpty
                    >
                        <MenuItem value={0}>No Battery</MenuItem>
                        {Object.entries(groupedBatteries).map(([manufacturer, items]) => [
                            manufacturer !== 'a' && (
                                <ListSubheader key={manufacturer}>{manufacturer}</ListSubheader>
                            ),
                            ...items.map(item => (
                                <MenuItem key={item.battery_id} value={item.battery_id}>
                                    {!item.outofstock && <Icon sx={{ color: green[500], fontSize: '14px', mr: 0.5 }}>done</Icon>}
                                    {item.outofstock && <Icon sx={{ color: red[500], fontSize: '14px', mr: 0.5 }}>do_not_disturb</Icon>}
                                    <span style={{ color: item.outofstock ? 'red' : 'black' }}>
                                        {item.description}
                                    </span>
                                </MenuItem>
                            ))
                        ])}
                    </Select>
                    <NumberInput
                        label="Qty"
                        value={batteryQty}
                        onChange={handleBatteryQtyChange}
                        min={1}
                        max={20}
                        decimalScale={0}
                        sx={{ width: 80 }}
                    />
                </Box>
            </Box>

            {/* Inverters Section */}
            <Box>
                <Typography sx={styles.sectionTitle}>Inverters</Typography>
                <Box sx={styles.checkboxRow}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={standardInverters} onChange={handleInverterTypeChange('standardinverters')} />}
                        label={<Typography sx={{ fontSize: 12 }}>String Inverters</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={hybrid} onChange={handleInverterTypeChange('hybrid')} />}
                        label={<Typography sx={{ fontSize: 12 }}>Hybrid</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={offgrid} onChange={handleInverterTypeChange('offgrid')} />}
                        label={<Typography sx={{ fontSize: 12 }}>Off Grid</Typography>}
                    />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={is3Phase} onChange={handleInverterTypeChange('is3phase')} />}
                        label={<Typography sx={{ fontSize: 12 }}>3 Phase</Typography>}
                    />
                </Box>

                <Typography sx={{ mt: 1, mb: 1, fontSize: 12 }}>
                    System Size: {(systemSize / 1000).toFixed(2)} kW
                </Typography>

                {/* Add Inverter Row */}
                <Box sx={styles.addRow}>
                    <Select
                        value={tempSelectedInverter}
                        onChange={handleTempInverterChange}
                        size="small"
                        sx={{ minWidth: 300, fontSize: 12 }}
                    >
                        <MenuItem value={-1}>Please Select An Inverter</MenuItem>
                        <MenuItem value={-2}>No Inverter Required</MenuItem>
                        {filteredInverters.map(inv => (
                            <MenuItem key={inv.id} value={inv.id}>
                                {!inv.outofstock && <Icon sx={{ color: green[500], fontSize: '14px', mr: 0.5 }}>done</Icon>}
                                {inv.outofstock && <Icon sx={{ color: red[500], fontSize: '14px', mr: 0.5 }}>do_not_disturb</Icon>}
                                <span style={{ color: inv.outofstock ? 'red' : 'black' }}>
                                    {inv.watts}W {inv.manufacturer} {inv.description} {inv.is3phase ? '3Phase' : 'Single'} {inv.hybrid ? 'Hybrid' : 'String'}
                                </span>
                            </MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleAddInverter}
                        disabled={!tempSelectedInverter || tempSelectedInverter < 0}
                    >
                        Add
                    </Button>
                </Box>

                {/* Inverters Table */}
                {requiredInverters.length > 0 && (
                    <TableContainer>
                        <Table size="small" sx={styles.inverterTable}>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Manufacturer / Description</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>kW</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>3Phase</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                                {requiredInverters.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {item.inverter?.manufacturer} - {item.inverter?.description}
                                        </TableCell>
                                        <TableCell align="center">
                                            {((item.inverter?.watts || 0) / 1000).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.inverter?.hybrid ? 'Hybrid' : item.solaredge ? 'Solar Edge' : 'String'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.inverter?.is3phase ? 'X' : ''}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => handleRemoveInverter(index)}>
                                                <Icon sx={{ color: red[500], fontSize: 14 }}>delete_forever</Icon>
                                            </IconButton>
                                            <Checkbox
                                                size="small"
                                                checked={item.overwrite || false}
                                                onChange={() => handleOverwriteChange(index)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {/* AC Controller Section (only show if battery selected) */}
            {battery && battery.battery_id > 1 && (
                <Box>
                    <Typography sx={styles.sectionTitle}>AC Controller / Charger</Typography>
                    <Select
                        value={acController?.item_id || 0}
                        onChange={handleACControllerChange}
                        size="small"
                        sx={styles.batterySelect}
                        displayEmpty
                    >
                        <MenuItem value={0}>No AC Controller</MenuItem>
                        {Object.entries(groupedACControllers).map(([manufacturer, items]) => [
                            manufacturer !== 'a' && (
                                <ListSubheader key={manufacturer}>{manufacturer}</ListSubheader>
                            ),
                            ...items.map(item => (
                                <MenuItem key={item.item_id} value={item.item_id}>
                                    {!item.outofstock && <Icon sx={{ color: green[500], fontSize: '14px', mr: 0.5 }}>done</Icon>}
                                    {item.outofstock && <Icon sx={{ color: red[500], fontSize: '14px', mr: 0.5 }}>do_not_disturb</Icon>}
                                    <span style={{ color: item.outofstock ? 'red' : 'black' }}>
                                        {item.description}
                                    </span>
                                </MenuItem>
                            ))
                        ])}
                    </Select>
                </Box>
            )}
        </Box>
    )
}

export default BatteryInvertersTab
